import React from 'react';
import { AdSettings, AdAsset, Hospital, isAssetActiveRightNow } from '../types';
import { Tv, Monitor, Smartphone, HeartPulse, PlayCircle, ExternalLink } from 'lucide-react';
import AdBanner from './AdBanner';
import AdTicker from './AdTicker';
import AdFullscreen from './AdFullscreen';

interface AdManagerPreviewProps {
    settings: AdSettings;
    assets: AdAsset[];
    hospital: Hospital;
    primaryColor: string;
}

// Simulated patient rows for the TV mockup
const MOCK_PATIENTS = [
    { code: 'MT001', status: 'EN CIRUGÍA', color: 'text-indigo-400', bg: 'bg-indigo-500/20', border: 'border-indigo-500/30' },
    { code: 'AB023', status: 'RECUPERACIÓN', color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
    { code: 'LM045', status: 'PREPARACIÓN', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
];

const AdManagerPreview: React.FC<AdManagerPreviewProps> = ({ settings, assets, hospital, primaryColor }) => {
    const [currentTime, setCurrentTime] = React.useState(new Date());

    React.useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const activeAssets = assets.filter(a => isAssetActiveRightNow(a, currentTime));

    return (
        <div className="bg-[#0a0c10] rounded-[2rem] p-6 space-y-8 border border-slate-800 shadow-2xl overflow-hidden h-full">
            <div className="flex items-center justify-between">
                <h3 className="text-white font-black flex items-center gap-2 text-md">
                    <PlayCircle className="w-4 h-4 text-indigo-400" /> Preview en Vivo
                </h3>
                <a href={`/${hospital.handle}/board`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-slate-300 bg-slate-800 hover:bg-indigo-500 hover:text-white transition-colors px-3 py-1.5 rounded-lg shadow-sm border border-slate-700">
                    <ExternalLink className="w-3.5 h-3.5" /> Abrir TV
                </a>
            </div>

            <div className="flex flex-col gap-8 items-center justify-start pb-4">
                {/* TV Normal Mockup — Realistic Board */}
                <div className="space-y-4 w-full flex flex-col items-center">
                    <p className="text-slate-400 text-[10px] font-bold text-center uppercase tracking-widest flex items-center gap-2">
                        <Tv className="w-3 h-3" /> TV Normal
                    </p>
                    <div className="w-full max-w-[360px] aspect-video bg-[#111318] rounded-xl overflow-hidden relative shadow-lg ring-2 ring-[#1f2937] border border-white/10">
                        <div className="absolute top-0 left-0 w-[1920px] h-[1080px] origin-top-left flex flex-col" style={{ transform: 'scale(0.1875)' }}>
                            {/* Header Bar */}
                            <div className="h-[100px] bg-[#0d1117] border-b border-white/5 flex items-center px-16 gap-6 shrink-0">
                                {hospital.branding?.logoDarkUrl || hospital.branding?.logoUrl ? (
                                    <img
                                        src={hospital.branding?.logoDarkUrl || hospital.branding?.logoUrl}
                                        alt="Logo"
                                        className="h-[60px] w-auto object-contain brightness-90"
                                    />
                                ) : (
                                    <div className="w-[60px] h-[60px] rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                                        <HeartPulse className="w-8 h-8" style={{ color: primaryColor }} />
                                    </div>
                                )}
                                <div>
                                    <p className="text-white/80 text-[28px] font-black tracking-tight leading-none">{hospital.name}</p>
                                    <p className="text-white/30 text-[16px] font-bold uppercase tracking-[0.2em] mt-1">Recepción Digital</p>
                                </div>
                                <div className="ml-auto flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
                                    <span className="text-white/40 text-[14px] font-bold uppercase tracking-widest">EN VIVO</span>
                                </div>
                            </div>

                            {/* Patient Table */}
                            <div className="flex-1 p-16 pt-10">
                                {/* Column Headers */}
                                <div className="flex justify-between items-center px-8 mb-6">
                                    <span className="text-white/30 text-[18px] font-bold uppercase tracking-[0.15em]">Paciente</span>
                                    <span className="text-white/30 text-[18px] font-bold uppercase tracking-[0.15em]">Estado</span>
                                </div>
                                {/* Patient Rows */}
                                <div className="space-y-4">
                                    {MOCK_PATIENTS.map((p, i) => (
                                        <div key={i} className="w-full h-[80px] bg-white/[0.03] rounded-2xl flex items-center px-8 justify-between border border-white/5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-[50px] h-[50px] rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                                                    <HeartPulse className="w-6 h-6" style={{ color: primaryColor, opacity: 0.7 }} />
                                                </div>
                                                <span className="text-white font-mono text-[26px] font-bold tracking-wider">{p.code}</span>
                                            </div>
                                            <div className={`px-6 py-2 rounded-full border text-[16px] font-bold ${p.color} ${p.bg} ${p.border}`}>
                                                {p.status}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Ad Area at Bottom */}
                            <div className="w-full flex-col mt-auto relative z-50 shrink-0">
                                {settings.showAdsOnTV && (
                                    <div className="w-full flex flex-col">
                                        <div className="px-12 pb-8">
                                            <AdBanner assets={activeAssets} variant="tv" theme="dark" primaryColor={primaryColor} intervalOverride={3} />
                                        </div>
                                        {settings.tickerEnabled && (
                                            <div className="w-full">
                                                <AdTicker assets={activeAssets} variant="tv" speed={settings.tickerSpeed as any} theme="dark" primaryColor={primaryColor} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile Mockup — Realistic Family View */}
                <div className="space-y-4 w-full flex flex-col items-center">
                    <p className="text-slate-400 text-[10px] font-bold text-center uppercase tracking-widest flex items-center gap-2">
                        <Smartphone className="w-3 h-3" /> Celular
                    </p>
                    <div className="w-[120px] h-[260px] bg-slate-50 rounded-[1.5rem] overflow-hidden relative shadow-lg ring-4 ring-slate-800 border-[3px] border-slate-900">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40%] h-3 bg-slate-900 rounded-b-lg z-[60]"></div>
                        <div className="absolute top-0 left-0 w-[400px] h-[866px] origin-top-left flex flex-col bg-slate-50" style={{ transform: 'scale(0.3)' }}>
                            {/* Mobile Header */}
                            <div className="h-24 bg-white border-b border-slate-200 flex items-center px-6 gap-3">
                                {hospital.branding?.logoUrl ? (
                                    <img src={hospital.branding.logoUrl} alt="Logo" className="h-8 w-auto object-contain" />
                                ) : (
                                    <HeartPulse className="w-6 h-6" style={{ color: primaryColor }} />
                                )}
                                <div>
                                    <p className="text-[14px] font-bold text-slate-800 leading-none">{hospital.name}</p>
                                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Seguimiento</p>
                                </div>
                            </div>
                            {/* Patient Card Simulated */}
                            <div className="flex-1 p-8 pb-32 space-y-5 overflow-y-auto">
                                {/* Code + Status */}
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Paciente</p>
                                    <p className="text-[22px] font-mono font-black text-slate-800 tracking-wider">MT001</p>
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">En Cirugía</span>
                                    </div>
                                    {/* Progress Bar */}
                                    <div className="mt-3">
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full rounded-full w-[65%] relative overflow-hidden" style={{ backgroundColor: primaryColor }}>
                                                <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
                                            </div>
                                        </div>
                                        <p className="text-[8px] text-slate-400 mt-1 text-right font-mono">65%</p>
                                    </div>
                                </div>
                                {/* Timeline Simulated */}
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">Timeline</p>
                                    {['Admisión', 'Preparación', 'Cirugía'].map((s, i) => (
                                        <div key={s} className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: i < 2 ? primaryColor : '#10b981' }}></div>
                                            <div className="flex-1">
                                                <p className="text-[10px] font-bold text-slate-700">{s}</p>
                                            </div>
                                            <span className="text-[8px] text-slate-400 font-mono">{['08:00', '09:15', '10:30'][i]}</span>
                                        </div>
                                    ))}
                                </div>
                                {/* Mobile Ad */}
                                {settings.showAdsOnMobile && (
                                    <div className="mt-4 space-y-4">
                                        <AdBanner assets={activeAssets} variant="mobile" theme="light" primaryColor={primaryColor} intervalOverride={3} />
                                    </div>
                                )}
                            </div>
                            {/* Mobile Ticker */}
                            {settings.showAdsOnMobile && settings.tickerEnabled && (
                                <div className="absolute bottom-0 left-0 w-full bg-slate-50 border-t border-slate-200 z-50">
                                    <AdTicker assets={activeAssets} variant="mobile" speed={settings.tickerSpeed as any} theme="light" primaryColor={primaryColor} />
                                    <div className="h-4 bg-slate-50"></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* TV Empty Mockup */}
                <div className="space-y-4 w-full flex flex-col items-center hidden xl:flex">
                    <p className="text-slate-400 text-[10px] font-bold text-center uppercase tracking-widest flex items-center gap-2">
                        <Monitor className="w-3 h-3" /> TV Vacía
                    </p>
                    <div className="w-full max-w-[360px] aspect-video bg-[#111318] rounded-xl overflow-hidden relative shadow-lg ring-2 ring-[#1f2937] border border-white/10">
                        <div className="absolute top-0 left-0 w-[1920px] h-[1080px] origin-top-left" style={{ transform: 'scale(0.1875)' }}>
                            {settings.showAdsOnTV && settings.fullscreenWhenEmpty && assets.some(a => a.type === 'FULLSCREEN_SLIDE' && a.isActive) ? (
                                <div className="w-full h-full relative">
                                    <AdFullscreen
                                        assets={activeAssets}
                                        adSettings={settings}
                                        hospitalName={hospital.name}
                                        hospitalLogo={hospital.branding?.logoUrl}
                                        hospitalLogoDark={hospital.branding?.logoDarkUrl}
                                        primaryColor={primaryColor}
                                        theme="dark"
                                    />
                                    {settings.tickerEnabled && (
                                        <div className="absolute bottom-0 left-0 w-full z-50">
                                            <AdTicker assets={activeAssets} variant="tv" speed={settings.tickerSpeed as any} theme="dark" primaryColor={primaryColor} />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-[#111318]">
                                    <div className="w-64 h-64 rounded-full bg-white/5 flex items-center justify-center mb-8">
                                        <HeartPulse className="w-32 h-32 text-slate-700" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdManagerPreview;
